// Event log: everything raised this session (state.log), newest date first with ties broken by
// priority, grouped under one header per turn, filterable by kind.

import { useSignal } from '@preact/signals';
import { EPOCH_START, addDays } from '../core/dates.ts';
import type { Notification, NotificationKind } from '../services/api.ts';
import { useServices } from './context.ts';
import { TURN_DAYS, fmtDate, fmtInt, turnOf } from './format.ts';
import { Icon, KIND_ICON } from './icons.tsx';
import { KIND_LABEL, NationTags, NoteLinks } from './NotificationStack.tsx';
import { bodyOf, noteClaim } from './notecard.ts';
import { ClaimLabel, Screen, SourceChip, useTip } from './parts.tsx';

const LOG_MAX = 300;

/** Newest date first; on the same date, higher priority first. */
function byDateThenPriority(a: Notification, b: Notification): number {
  if (a.date !== b.date) {
    return a.date < b.date ? 1 : -1;
  }
  return b.priority - a.priority;
}

interface TurnGroup {
  turn: number;
  rows: Notification[];
}

function groupByTurn(rows: Notification[]): TurnGroup[] {
  const groups: TurnGroup[] = [];
  for (const n of rows) {
    const turn = turnOf(n.date);
    const last = groups[groups.length - 1];
    if (last && last.turn === turn) {
      last.rows.push(n);
      continue;
    }
    groups.push({ turn, rows: [n] });
  }
  return groups;
}

export function LogPanel() {
  const { state, actions } = useServices();
  const only = useSignal<NotificationKind | null>(null);
  const all = state.log.value;
  const kinds = [...new Set(all.map((n) => n.kind))];
  const rows = all
    .filter((n) => only.value === null || n.kind === only.value)
    .sort(byDateThenPriority)
    .slice(0, LOG_MAX);
  const groups = groupByTurn(rows);

  return (
    <Screen title="Event log" icon="log" subtitle={`${all.length} entries this session`} class="wc-screen--narrow wc-log-screen" onClose={() => actions.openPanel('none')}>
      <div class="wc-log-filters" role="toolbar" aria-label="Filter by kind">
        <button type="button" class="wc-filter" aria-pressed={only.value === null} onClick={() => (only.value = null)}>
          All
        </button>
        {kinds.map((k) => (
          <KindFilter key={k} kind={k} active={only.value === k} onToggle={() => (only.value = only.value === k ? null : k)} />
        ))}
      </div>
      <div class="wc-log wc-scroll wc-fade">
        {rows.length === 0 ? <div class="wc-empty">Nothing yet. Advance a turn to raise events.</div> : null}
        {groups.map((g) => (
          <section key={g.turn} class="wc-log-group">
            <h3 class="wc-log-turn">
              <span>Turn {fmtInt(g.turn)}</span>
              <span class="wc-year">week of {fmtDate(addDays(EPOCH_START, (g.turn - 1) * TURN_DAYS))}</span>
            </h3>
            <ol class="wc-log-list">
              {g.rows.map((n) => (
                <LogRow key={n.id} note={n} />
              ))}
            </ol>
          </section>
        ))}
      </div>
    </Screen>
  );
}

function KindFilter({ kind, active, onToggle }: { kind: NotificationKind; active: boolean; onToggle: () => void }) {
  const handlers = useTip(() => <div class="wc-tip-title">{KIND_LABEL[kind]}</div>, 'below');
  return (
    <button type="button" class="wc-filter" aria-pressed={active} aria-label={KIND_LABEL[kind]} onClick={onToggle} {...handlers}>
      <Icon name={KIND_ICON[kind]} size={15} />
    </button>
  );
}

function LogRow({ note }: { note: Notification }) {
  const { actions } = useServices();
  const expanded = useSignal(false);
  const body = bodyOf(note.title, note.body);
  const claim = noteClaim(note.id, note.date);
  return (
    <li class="wc-log-row" data-kind={note.kind}>
      <span class="wc-log-icon">
        <Icon name={KIND_ICON[note.kind]} size={18} />
      </span>
      <span class="wc-log-date">{fmtDate(note.date)}</span>
      <span class="wc-log-text">
        <b>{note.title}</b>
        {body !== null ? <span class="wc-log-body">{body}</span> : null}
        {claim ? <ClaimLabel kind={claim.kind} text={claim.text} /> : null}
        {note.iso3s.length > 0 ? <NationTags iso3s={note.iso3s} /> : null}
      </span>
      <span class="wc-log-actions">
        <SourceChip ids={note.sources} />
        <NoteLinks links={note.links} expanded={expanded} />
        {note.hex !== undefined ? (
          <button type="button" class="wc-ibtn wc-log-fly" aria-label="Fly to" onClick={() => actions.flyTo(note.hex as number)}>
            <Icon name="target" size={14} />
          </button>
        ) : null}
      </span>
    </li>
  );
}
